import { FileText, ChevronLeft, ChevronRight, Eye, EyeOff, X } from 'lucide-react';
import { ContractHtmlPreview } from './ContractHtmlPreview';
import type { AssistanceEncart } from '@/features/assistance/assistanceWording';
import { usePdfPreviewStore } from '../stores/pdfPreviewStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import type { ContractFormState } from '@/features/contracts/hooks/useContractFormWizard';

interface PdfPreviewPanelProps {
  formData: ContractFormState;
  warrantyName?: string;
  warrantyDuration?: number;
  warrantyFinancialCap?: string;
  warrantyKmLimit?: string;
  warrantyType?: number;
  warrantyParts?: any[];
  currentStep?: number;
  guarantorName?: string;
  guarantorSiren?: string;
  guarantorAddress?: string;
  guarantorCpCity?: string;
  guarantorPhone?: string;
  guarantorEmail?: string;
  isIntermediary?: boolean;
  diagnostic?: string;
  paymentMethodText?: string;
  logoUrl?: string | null;
  /** Encart d'assistance composé par le wizard, aligné sur le PDF définitif. */
  assistanceEncart?: AssistanceEncart | null;
}

/** Max panel width — used as cap for dynamic calculation */
export const PDF_PANEL_WIDTH = 600;

/** Width of the tab button attached to the panel's left edge */
export const PDF_TAB_WIDTH = 56;

/**
 * Hook to get the responsive panel width.
 * Returns 0 on mobile/tablet (overlay mode), dynamic px on desktop.
 * Same pattern as DeliveryProofPdfPreviewPanel.
 */
export function usePdfPanelWidth(): number {
  const breakpoint = useBreakpoint();
  if (breakpoint !== 'lg') return 0;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  return Math.min(PDF_PANEL_WIDTH, Math.max(340, Math.round(vw * 0.38)));
}

/**
 * Fixed right panel showing a live HTML preview of the contract PDF.
 * Responsive: side panel on desktop, full-screen overlay on mobile/tablet.
 */
export function PdfPreviewPanel({
  formData,
  warrantyName,
  warrantyDuration,
  warrantyFinancialCap,
  warrantyKmLimit,
  warrantyType,
  warrantyParts,
  currentStep,
  guarantorName,
  guarantorSiren,
  guarantorAddress,
  guarantorCpCity,
  guarantorPhone,
  guarantorEmail,
  isIntermediary,
  diagnostic,
  paymentMethodText,
  logoUrl,
  assistanceEncart,
}: PdfPreviewPanelProps) {
  const { isOpen, toggle, close } = usePdfPreviewStore();
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === 'lg';

  const panelWidthPx = usePdfPanelWidth();

  const previewContent = (
    <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200 tw-overflow-hidden">
      <ContractHtmlPreview
        formData={formData}
        warrantyName={warrantyName}
        warrantyDuration={warrantyDuration}
        warrantyFinancialCap={warrantyFinancialCap}
        warrantyKmLimit={warrantyKmLimit}
        warrantyType={warrantyType}
        warrantyParts={warrantyParts}
        currentStep={currentStep}
        guarantorName={guarantorName}
        guarantorSiren={guarantorSiren}
        guarantorAddress={guarantorAddress}
        guarantorCpCity={guarantorCpCity}
        guarantorPhone={guarantorPhone}
        guarantorEmail={guarantorEmail}
        isIntermediary={isIntermediary}
        diagnostic={diagnostic}
        paymentMethodText={paymentMethodText}
        logoUrl={logoUrl}
        assistanceEncart={assistanceEncart}
      />
    </div>
  );

  // ===================== MOBILE / TABLET : overlay plein écran =====================
  if (!isDesktop) {
    return (
      <>
        {/* FAB — floating action button */}
        <button
          type="button"
          onClick={toggle}
          className="tw-fixed tw-bottom-24 tw-right-4 tw-z-50 tw-w-14 tw-h-14 tw-rounded-full tw-bg-affinicar-primary tw-text-white tw-shadow-lg tw-flex tw-items-center tw-justify-center hover:tw-bg-affinicar-dark tw-transition-colors"
          title={isOpen ? "Fermer l'aperçu" : "Aperçu du contrat"}
        >
          {isOpen ? <EyeOff className="tw-w-6 tw-h-6" /> : <Eye className="tw-w-6 tw-h-6" />}
        </button>

        {/* Overlay backdrop */}
        {isOpen && (
          <div
            className="tw-fixed tw-inset-0 tw-z-40 tw-bg-black/50"
            onClick={close}
          />
        )}

        {/* Full-screen panel */}
        <div
          className={`tw-fixed tw-inset-0 tw-top-16 tw-z-50 tw-flex tw-flex-col tw-bg-gray-50 tw-transition-transform tw-duration-300 tw-ease-in-out ${isOpen ? 'tw-translate-x-0' : 'tw-translate-x-full'} ${isOpen ? 'tw-pointer-events-auto' : 'tw-pointer-events-none'}`}
        >
          {/* Header bar */}
          <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-bg-white tw-flex-shrink-0">
            <div className="tw-flex tw-items-center tw-gap-2">
              <FileText className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
              <span className="tw-text-sm tw-font-semibold tw-text-gray-900">Aperçu du contrat</span>
            </div>
            <button
              type="button"
              onClick={close}
              className="tw-p-2 tw-text-gray-500 hover:tw-text-gray-700 tw-rounded-lg hover:tw-bg-gray-100 tw-transition-colors"
            >
              <X className="tw-w-5 tw-h-5" />
            </button>
          </div>

          {/* Preview content */}
          <div className="tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-p-3">
            {previewContent}
          </div>
        </div>
      </>
    );
  }

  // ===================== DESKTOP : side-by-side panel =====================
  return (
    <div
      className="tw-fixed tw-top-16 tw-right-0 tw-h-[calc(100vh-4rem)] tw-z-40 tw-transition-all tw-duration-300 tw-ease-in-out tw-flex tw-flex-col tw-bg-gray-50 tw-border-l tw-border-gray-200 tw-shadow-xl"
      style={{
        width: panelWidthPx,
        transform: isOpen ? 'translateX(0)' : `translateX(${panelWidthPx}px)`,
      }}
    >
      {/* Tab button — attached to panel's left edge, moves with panel */}
      <button
        type="button"
        onClick={toggle}
        className="tw-absolute tw-top-1/2 tw-left-0 -tw-translate-x-full -tw-translate-y-1/2 tw-z-10 tw-w-14 tw-py-4 tw-px-2 tw-flex tw-flex-col tw-items-center tw-gap-3 tw-rounded-l-xl tw-cursor-pointer tw-transition-all tw-duration-200 tw-ease-out tw-hidden sm:tw-flex hover:tw-w-16"
        style={{
          background: isOpen
            ? 'linear-gradient(180deg, #4a52a0 0%, #6b60a0 100%)'
            : 'linear-gradient(180deg, #575fa7 0%, #8074af 100%)',
          boxShadow: '-4px 2px 16px rgba(87, 95, 167, 0.4), inset 0 0 0 1px rgba(255, 255, 255, 0.1)',
        }}
        title={isOpen ? "Fermer l'aperçu" : "Aperçu du contrat"}
      >
        <FileText className="tw-w-6 tw-h-6 tw-text-white" />
        <span className="tw-text-white tw-text-sm tw-font-semibold [writing-mode:vertical-lr] tw-rotate-180">
          {isOpen ? "Fermer l'aperçu" : "Aperçu du contrat"}
        </span>
        {isOpen
          ? <ChevronRight className="tw-w-5 tw-h-5 tw-text-white/60" />
          : <ChevronLeft className="tw-w-5 tw-h-5 tw-text-white/60" />
        }
      </button>

      {/* HTML Preview */}
      <div className={`tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-p-3 ${isOpen ? '' : 'tw-pointer-events-none'}`}>
        {previewContent}
      </div>
    </div>
  );
}
